import { useLocaleTag } from "@/lib/i18n/format";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactNode } from "react";
import { type CalculatorState, UNIT_PRESETS } from "./pricing-calculator";
import { DEMO_HREF, T } from "./pricing-shared";

/** Units slider + quick-pick presets. `compact` stacks the presets full-width. */
export function UnitsControl({
  calc,
  compact,
}: {
  readonly calc: CalculatorState;
  readonly compact: boolean;
}): ReactNode {
  const localeTag = useLocaleTag();
  const t = useTranslations("public.pricingCalcShell");
  const presetButtons = UNIT_PRESETS.map((n) => {
    const active = calc.units === n;
    return (
      <button
        key={n}
        type="button"
        onClick={() => calc.setUnits(n)}
        className="mkpreise-preset"
        style={{
          appearance: "none",
          cursor: "pointer",
          fontFamily: T.sans,
          fontSize: compact ? 12.5 : 13,
          fontWeight: 600,
          padding: compact ? "8px 0" : "8px 14px",
          borderRadius: 9,
          border: `1px solid ${active ? T.ink : "rgba(11,15,25,0.16)"}`,
          background: active ? T.ink : "#ffffff",
          color: active ? "#ffffff" : T.ink,
          flex: compact ? 1 : "none",
        }}
      >
        {n.toLocaleString(localeTag)}
      </button>
    );
  });

  const slider = (
    <div style={{ flex: 1, minWidth: compact ? undefined : 300 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 13,
        }}
      >
        <span
          style={{
            fontFamily: T.label,
            fontSize: compact ? 10.5 : 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(11,15,25,0.55)",
          }}
        >
          {t("verwaltungseinheiten")}
        </span>
        <span
          style={{
            fontFamily: T.display,
            fontSize: compact ? 32 : 34,
            lineHeight: 1,
            color: T.ink,
          }}
        >
          {calc.unitsLabel}
        </span>
      </div>
      <input
        type="range"
        min={100}
        max={10000}
        step={50}
        value={calc.units}
        onChange={(e) => calc.setUnits(Number(e.target.value))}
        className="mkpreise-price-range"
        style={{
          width: "100%",
          height: 6,
          borderRadius: 999,
          background: calc.unitsTrackBg,
          cursor: "pointer",
          margin: "2px 0 8px",
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontFamily: T.label,
          fontSize: 10.5,
          color: "rgba(11,15,25,0.4)",
        }}
      >
        <span>100</span>
        <span>10.000</span>
      </div>
    </div>
  );

  if (compact) {
    return (
      <div
        style={{
          background: "#FAFAFA",
          border: "1px solid rgba(11,15,25,0.10)",
          borderRadius: 16,
          padding: "18px 18px",
          marginBottom: 16,
        }}
      >
        {slider}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 9,
            marginTop: 14,
          }}
        >
          <span
            style={{
              fontFamily: T.label,
              fontSize: 10,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#9AA0A6",
            }}
          >
            {t("schnell")}
          </span>
          <div style={{ display: "flex", gap: 7 }}>{presetButtons}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#FAFAFA",
        border: "1px solid rgba(11,15,25,0.10)",
        borderRadius: 16,
        padding: "22px 26px",
        marginBottom: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 34,
          flexWrap: "wrap",
        }}
      >
        {slider}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <span
            style={{
              fontFamily: T.label,
              fontSize: 10,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#9AA0A6",
            }}
          >
            {t("schnellwahl")}
          </span>
          <div style={{ display: "flex", gap: 7 }}>{presetButtons}</div>
        </div>
      </div>
    </div>
  );
}

/** Dark "Deine Ersparnis" banner with CTA. */
export function SavingsBanner({
  calc,
  compact,
}: {
  readonly calc: CalculatorState;
  readonly compact: boolean;
}): ReactNode {
  const t = useTranslations("public.pricingCalcShell");
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        marginBottom: compact ? 16 : 20,
        borderRadius: 16,
        background: T.ink,
        color: "#fff",
        padding: compact ? "22px 20px" : "26px 30px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 28,
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1.4px)",
          backgroundSize: "6px 6px",
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative" }}>
        <div
          style={{
            fontFamily: T.label,
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: T.mint,
            marginBottom: compact ? 8 : 10,
          }}
        >
          {t("ersparnis")}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily: T.display,
              fontWeight: 600,
              fontSize: compact ? 46 : 52,
              lineHeight: 0.9,
              letterSpacing: "-0.02em",
            }}
          >
            {calc.saveMoLabel} €
          </span>
          <span
            style={{
              fontFamily: T.display,
              fontWeight: 600,
              fontSize: compact ? 46 : 52,
              lineHeight: 0.9,
              letterSpacing: "-0.02em",
              color: "#fff",
            }}
          >
            {t("monat")}
          </span>
          {/* One flex item: moves to the next line whole; "≈" never ends a line alone. */}
          <span
            style={{
              fontFamily: T.display,
              fontWeight: 600,
              fontSize: compact ? 30 : 36,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              color: "#fff",
            }}
          >
            <span style={{ whiteSpace: "nowrap" }}>
              <span
                style={{
                  fontFamily: T.sans,
                  fontWeight: 400,
                  letterSpacing: 0,
                  color: "rgba(255,255,255,0.6)",
                  marginRight: 12,
                }}
              >
                ≈
              </span>
              <span>{calc.saveYrLabel} €</span>
            </span>{" "}
            {t("proJahr")}
          </span>
        </div>
      </div>
      <Link
        href={DEMO_HREF}
        className="mkpreise-btn-light"
        style={{
          position: "relative",
          background: "#ffffff",
          color: T.ink,
          padding: compact ? "13px 22px" : "15px 26px",
          borderRadius: 11,
          fontFamily: T.sans,
          fontSize: compact ? 14.5 : 15,
          fontWeight: 600,
          textDecoration: "none",
          display: "inline-flex",
          alignItems: "center",
          gap: 9,
          whiteSpace: "nowrap",
        }}
      >
        {t("demoBuchen")} <span style={{ fontSize: 13 }}>↗</span>
      </Link>
    </div>
  );
}

/** Header for a comparison card (eyebrow + title + sub on the left, total on the right). */
export function CompareHeader({
  eyebrow,
  eyebrowColor,
  title,
  sub,
  total,
  totalUnit,
  totalUnitColor,
  yearLabel,
  compact,
}: {
  readonly eyebrow: string;
  readonly eyebrowColor: string;
  readonly title: string;
  readonly sub?: string;
  readonly total: string;
  readonly totalUnit: string;
  readonly totalUnitColor: string;
  readonly yearLabel: string;
  readonly compact: boolean;
}): ReactNode {
  const t = useTranslations("public.pricingCalcShell");
  const titleBlock = (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontFamily: T.label,
          fontSize: 10.5,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: eyebrowColor,
          marginBottom: 5,
        }}
      >
        {eyebrow}
      </div>
      <div
        style={{
          fontFamily: T.display,
          fontSize: compact ? 19 : 21,
          lineHeight: 1.2,
          color: T.ink,
        }}
      >
        {title}
      </div>
      {sub ? (
        <div
          style={{
            fontFamily: T.sans,
            fontSize: 12,
            color: "#8A8D94",
            marginTop: 3,
          }}
        >
          {sub}
        </div>
      ) : null}
    </div>
  );
  const totalLine = (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 5,
        justifyContent: compact ? "flex-start" : "flex-end",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          fontFamily: T.display,
          fontSize: compact ? 27 : 30,
          lineHeight: 1,
          color: T.ink,
        }}
      >
        {total} €
      </span>
      <span
        style={{
          fontFamily: T.sans,
          fontSize: compact ? 12 : 14,
          fontWeight: 600,
          color: totalUnitColor,
        }}
      >
        {totalUnit}
      </span>
      <span
        style={{
          fontFamily: T.sans,
          fontSize: 10,
          color: "#9AA0A6",
          fontWeight: 400,
        }}
      >
        {t("netto")}
      </span>
    </div>
  );
  const yearLine = (
    <div
      style={{
        fontFamily: T.label,
        fontSize: 10.5,
        color: "#9AA0A6",
        marginTop: compact ? 0 : 4,
        whiteSpace: "nowrap",
      }}
    >
      ≈ {yearLabel} € {t("jahr")}
    </div>
  );

  // Phone width can't fit title and total side by side: stack them.
  if (compact) {
    return (
      <div style={{ padding: "18px 18px 10px" }}>
        {titleBlock}
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            flexWrap: "wrap",
            columnGap: 12,
            rowGap: 4,
            marginTop: 12,
          }}
        >
          {totalLine}
          {yearLine}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        padding: "20px 22px 12px",
      }}
    >
      {titleBlock}
      <div style={{ textAlign: "right", flex: "none" }}>
        {totalLine}
        {yearLine}
      </div>
    </div>
  );
}
